import React, { useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Lock, Plus } from "lucide-react";

import { Elements } from "@stripe/react-stripe-js";

import CardSection from "../../common/StripeCardForm";
import DialogBox from "../Dashboard/cardPopup";
import AddCardForm from "../Dashboard/addCardForm";
import { stripePromise } from "../../helpers/constants";
import { Loader } from "../../common/loader";
import { Button } from "../../common/customDesignedComponents/button";
import { Input } from "../../common/customDesignedComponents/input";
import {
  getObjLength,
  getStoredPromoCode,
  persistStoredPromoCode,
  clearStoredPromoCode,
} from "../../helpers/utils";
import { attachEnhancedToCompleteUpgradeFlags } from "../../helpers/generatePropertySecurityQuestions";
import {
  cardToSelect,
  isSavedCardExpired,
  SavedCardList,
} from "./savedCardSelection";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import { userProfileAction } from "../../../redux/slices/user/userProfileSlice";
import {
  hasAppliedPromoDiscount,
  isValidPaymentSummaryData,
  OrderSummaryBreakdown,
} from "../../common/customDesignedComponents/OrderSummaryModal";

const InlinePaymentCheckout = ({
  cardData,
  addPromoCode = false,
  promoCodeValue = () => {},
  uid = null,
  userId = null,
  selectedCategory = "complete_protection",
  selectedData = {},
  propertiesIds = undefined,
  default_card_id = undefined,
  userEmail = undefined,
  isTailwindComponent = false,
  stripeErr = "",
  validateErr = () => {},
  showInlineCardForm = true,
  callListingAPI = undefined,
  from_admin = false,
  from = "",
  isUserCreateProperty = false,
  isUpgradeCategory = false,
  isEnhancedToCompleteProtection = false,
  finalAmount = null,
  onFinalAmountChange = () => {},
}) => {
  const dispatch = useDispatch();
  const [selectedCardId, setSelectedCardId] = useState("");
  const [useNewCard, setUseNewCard] = useState(false);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [deferAddCardModal, setDeferAddCardModal] = useState(false);
  const [inlinePromoCode, setInlinePromoCode] = useState(
    () => getStoredPromoCode(),
  );
  const [appliedPromoCode, setAppliedPromoCode] = useState(() => {
    const stored = getStoredPromoCode();
    return stored && hasAppliedPromoDiscount(finalAmount) ? stored : "";
  });
  const [promoMessage, setPromoMessage] = useState("");
  const [promoMessageIsError, setPromoMessageIsError] = useState(false);
  const [livePricingData, setLivePricingData] = useState(finalAmount);
  const promoApplyRef = useRef(false);
  const promoRemoveRef = useRef(false);
  const pendingApplyCodeRef = useRef("");
  const hydratedPromoRef = useRef(false);

  const notifyPromoCodeValue = (code) => {
    if (typeof promoCodeValue === "function") {
      promoCodeValue(code);
    }
  };

  useEffect(() => {
    if (!deferAddCardModal) return undefined;
    const timer = window.setTimeout(() => {
      setIsAddCardModalOpen(true);
      setDeferAddCardModal(false);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [deferAddCardModal]);

  const cardDetails = useSelector((state) => state.userProfile.cardDetails);
  const cardLoader = useSelector((state) => state.userProfile.cardLoader);
  const reqLoader = useSelector((state) => state.properties.reqLoader);
  const {
    totalPaymentDetails,
    getTotalAmountLoader,
    statesLoader,
    investorsID,
    investorsYearlyData,
  } = useSelector((state) => state.signUpUser);
  const { selectedPropertiesData, selectedProperty } = useSelector(
    (state) => state?.properties,
  );
  const { investorReportResultData, invToken } = useSelector(
    (state) => state?.propertyRiskManagement,
  );

  const savedCards = useMemo(
    () => (Array.isArray(cardDetails?.data) ? cardDetails.data : []),
    [cardDetails?.data],
  );
  const hasSavedCards = savedCards.length > 0;

  const selectCard = (card) => {
    if (isSavedCardExpired(card)) return;
    setUseNewCard(false);
    setSelectedCardId(card.id);
    cardData([card]);
  };

  useEffect(() => {
    const target = cardToSelect(savedCards, default_card_id);
    if (target) {
      setUseNewCard(false);
      setSelectedCardId(target.id);
      cardData([target]);
      return;
    }

    if (!hasSavedCards) {
      setUseNewCard(true);
      cardData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardData, default_card_id, hasSavedCards, savedCards]);

  useEffect(() => {
    if (isValidPaymentSummaryData(finalAmount)) {
      setLivePricingData(finalAmount);
      const stored = getStoredPromoCode();
      if (stored && hasAppliedPromoDiscount(finalAmount)) {
        setInlinePromoCode(stored);
        setAppliedPromoCode(stored);
        notifyPromoCodeValue(stored);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from Order Summary finalAmount + stored promo
  }, [finalAmount]);

  useEffect(() => {
    const stored = getStoredPromoCode();
    if (!stored) return;
    setInlinePromoCode((current) => current || stored);
    if (
      hasAppliedPromoDiscount(finalAmount) ||
      hasAppliedPromoDiscount(livePricingData)
    ) {
      setAppliedPromoCode(stored);
      notifyPromoCodeValue(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- autofill once from Order Summary promo
  }, []);

  useEffect(() => {
    if (!getObjLength(totalPaymentDetails)) return;

    const promoErrorMessage =
      totalPaymentDetails?.data?.message ||
      totalPaymentDetails?.message ||
      "Unable to apply promo code.";

    if (promoRemoveRef.current) {
      if (
        totalPaymentDetails?.success &&
        isValidPaymentSummaryData(totalPaymentDetails?.data)
      ) {
        promoRemoveRef.current = false;
        setPromoMessage("");
        setPromoMessageIsError(false);
        setAppliedPromoCode("");
        clearStoredPromoCode();
        notifyPromoCodeValue("");
        setLivePricingData(totalPaymentDetails.data);
        onFinalAmountChange(totalPaymentDetails.data);
        return;
      }
      promoRemoveRef.current = false;
      setPromoMessage(
        totalPaymentDetails?.data?.message ||
          totalPaymentDetails?.message ||
          "Unable to remove promo code.",
      );
      setPromoMessageIsError(true);
      return;
    }

    if (promoApplyRef.current) {
      promoApplyRef.current = false;
      const nextData = totalPaymentDetails?.data;
      const code =
        pendingApplyCodeRef.current ||
        inlinePromoCode.trim() ||
        appliedPromoCode;
      const discountApplied =
        totalPaymentDetails?.success && hasAppliedPromoDiscount(nextData);

      if (discountApplied && isValidPaymentSummaryData(nextData)) {
        if (code) {
          persistStoredPromoCode(code);
          notifyPromoCodeValue(code);
          setAppliedPromoCode(code);
        }
        pendingApplyCodeRef.current = "";
        setPromoMessage("Promo code applied.");
        setPromoMessageIsError(false);
        setLivePricingData(nextData);
        onFinalAmountChange(nextData);
        return;
      }

      // Invalid / failed promo — keep last good pricing, show error, do not
      // send this code to payment APIs.
      clearStoredPromoCode();
      notifyPromoCodeValue("");
      setAppliedPromoCode("");
      pendingApplyCodeRef.current = "";
      setPromoMessage(promoErrorMessage);
      setPromoMessageIsError(true);
      return;
    }

    // Hydrate / sync payment-screen pricing (including Order Summary promo re-fetch)
    if (
      totalPaymentDetails?.success &&
      isValidPaymentSummaryData(totalPaymentDetails?.data)
    ) {
      setLivePricingData(totalPaymentDetails.data);
      onFinalAmountChange(totalPaymentDetails.data);
      const stored = getStoredPromoCode();
      if (stored && hasAppliedPromoDiscount(totalPaymentDetails.data)) {
        setInlinePromoCode(stored);
        setAppliedPromoCode(stored);
        notifyPromoCodeValue(stored);
      }
      return;
    }

    if (addPromoCode && !totalPaymentDetails?.success) {
      setPromoMessage(promoErrorMessage);
      setPromoMessageIsError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to payment details response
  }, [totalPaymentDetails]);

  useEffect(() => {
    return () => {
      dispatch(userSigupAction.fetchTotalPaymentDetails({}));
    };
  }, [dispatch]);

  const buildFinalAmountPayload = (promoCode) => {
    let planData = {};
    try {
      planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    } catch {
      planData = {};
    }
    let data = { ...planData };
    let url = "customer/get-final-amount";
    if (selectedCategory === "enhanced_monitoring") {
      url = "get-final-amount-enhanced-monitoring";
    }
    const resolvedPromo = promoCode || getStoredPromoCode() || null;
    if (resolvedPromo) {
      data.promo_code = resolvedPromo;
    } else {
      delete data.promo_code;
    }
    if (selectedCategory !== "enhanced_monitoring") {
      data = attachEnhancedToCompleteUpgradeFlags(data, {
        property: selectedProperty || selectedData,
        forceUpgrade: Boolean(isEnhancedToCompleteProtection),
      });
    }
    if (investorsID || invToken) {
      url = "investors-price-details";
      data = {
        user_id: userId || investorReportResultData?.data?.user?.id,
        property_id: selectedPropertiesData?.properties_Id ||
          propertiesIds || [selectedProperty?.property_id],
        plan_id: investorsYearlyData?.plan_id,
      };
      if (resolvedPromo) {
        data.promo_code = resolvedPromo;
      }
      if (invToken) {
        delete data?.property_ids;
      }
    }
    return { url, data };
  };

  // If payment screen opens without retained Order Summary pricing, refetch it
  useEffect(() => {
    const shouldHydratePricing =
      Boolean(isUpgradeCategory) ||
      Boolean(isEnhancedToCompleteProtection) ||
      Boolean(isUserCreateProperty) ||
      from === "add-subscriptions" ||
      from === "add-property";
    if (!shouldHydratePricing) return;
    if (promoApplyRef.current || promoRemoveRef.current) return;
    if (hydratedPromoRef.current) return;

    let planData = {};
    try {
      planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    } catch {
      planData = {};
    }
    if (!getObjLength(planData)) return;

    const carriedPromo =
      getStoredPromoCode() ||
      (typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem("ep_carried_promo") || ""
        : "") ||
      inlinePromoCode.trim() ||
      "";

    const hasValidPricing =
      isValidPaymentSummaryData(livePricingData) ||
      isValidPaymentSummaryData(finalAmount);

    if (hasValidPricing && !carriedPromo) {
      return;
    }

    hydratedPromoRef.current = true;
    if (carriedPromo) {
      persistStoredPromoCode(carriedPromo);
      try {
        sessionStorage.setItem("ep_carried_promo", carriedPromo);
      } catch {
        // ignore
      }
      setInlinePromoCode(carriedPromo);
      notifyPromoCodeValue(carriedPromo);
    }

    const { url, data } = buildFinalAmountPayload(carriedPromo || null);
    try {
      localStorage.setItem("Plan_Data", JSON.stringify(data));
    } catch {
      // ignore
    }
    dispatch(userSigupAction.getTotalPaymentDetails({ url, data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once on mount when pricing/promo needs sync
  }, [
    from,
    isUserCreateProperty,
    isUpgradeCategory,
    isEnhancedToCompleteProtection,
  ]);

  const refreshCards = () => {
    // Prefer parent listing callback (admin/SP-client/customer) — do not race
    // a second customer/card/list request that can overwrite Redux.
    if (typeof callListingAPI === "function") {
      callListingAPI();
      return;
    }
    if (from_admin && uid) {
      dispatch(
        userProfileAction.userCardDetails({
          url: `admin/users/${uid}/card`,
          data: {},
          token: true,
        }),
      );
      return;
    }
    dispatch(
      userProfileAction.userCardDetails({
        url: "customer/card/list",
        data: {},
        token: true,
      }),
    );
  };

  const applyPromoCode = () => {
    const code = inlinePromoCode.trim();
    if (!code) return;

    promoApplyRef.current = true;
    promoRemoveRef.current = false;
    pendingApplyCodeRef.current = code;
    setPromoMessage("");
    setPromoMessageIsError(false);

    const { url, data } = buildFinalAmountPayload(code);
    dispatch(userSigupAction.getTotalPaymentDetails({ url, data }));
  };

  const removePromoCode = () => {
    promoRemoveRef.current = true;
    promoApplyRef.current = false;
    pendingApplyCodeRef.current = "";
    clearStoredPromoCode();
    setInlinePromoCode("");
    setAppliedPromoCode("");
    setPromoMessage("");
    setPromoMessageIsError(false);
    notifyPromoCodeValue("");

    const { url, data } = buildFinalAmountPayload(null);
    try {
      localStorage.setItem("Plan_Data", JSON.stringify(data));
    } catch {
      // ignore
    }
    dispatch(userSigupAction.getTotalPaymentDetails({ url, data }));
  };

  const totalDueToday =
    totalPaymentDetails?.success && totalPaymentDetails?.data?.total != null
      ? Number(totalPaymentDetails.data.total).toFixed(2)
      : null;

  const pricingSummaryData = isValidPaymentSummaryData(livePricingData)
    ? livePricingData
    : isValidPaymentSummaryData(finalAmount)
      ? finalAmount
      : totalPaymentDetails?.success &&
          isValidPaymentSummaryData(totalPaymentDetails?.data)
        ? totalPaymentDetails.data
        : livePricingData && getObjLength(livePricingData)
          ? livePricingData
          : finalAmount && getObjLength(finalAmount)
            ? finalAmount
            : null;
  // Admin Add Property/Subscription and any Upgrade (customer or admin) payment
  // Add Property / Add Subscription / Upgrade (customer or admin)
  const showAdminPricingBreakdown =
    Boolean(isUpgradeCategory) ||
    Boolean(isEnhancedToCompleteProtection) ||
    Boolean(isUserCreateProperty) ||
    from === "add-subscriptions" ||
    from === "add-property";
  const promoIsActive =
    hasAppliedPromoDiscount(pricingSummaryData) || Boolean(appliedPromoCode);

  return (
    <div className="tailwind_component w-full min-w-0 pb-2 sm:px-4 sm:pb-3">
      <div className="mb-4 sm:mb-6 w-full min-w-0 sm:px-1">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-medium text-foreground">
            Payment information
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-secondary border-secondary hover:bg-secondary/10 shrink-0"
            onClick={() => setDeferAddCardModal(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add new card
          </Button>
        </div>

        {cardLoader && !hasSavedCards ? (
          <p className="text-sm text-muted-foreground">Loading saved cards…</p>
        ) : null}

        {/* Saved card list */}
        {hasSavedCards ? (
          <SavedCardList
            cards={savedCards}
            selectedCardId={useNewCard ? null : selectedCardId}
            defaultCardId={default_card_id}
            onSelect={selectCard}
          />
        ) : (
          <p className="text-sm text-muted-foreground mb-4">
            No saved cards yet. Add a card to your account.
          </p>
        )}

        {/* Pay with a different card */}
        {/* {hasSavedCards && showInlineCardForm ? (
          <button
            type="button"
            onClick={() => {
              setUseNewCard(true);
              setSelectedCardId("");
              cardData([]);
            }}
            className={`w-full text-left p-4 rounded-xl border transition-colors mb-4 ${
              useNewCard
                ? "bg-secondary/5 border-secondary ring-1 ring-secondary/30"
                : "border-dashed border-border hover:border-secondary/40"
            }`}
          >
            <span className="text-sm font-medium text-foreground">
              Pay with a different card
            </span>
          </button>
        ) : null}

        {showInlineCardForm &&
        !isAddCardModalOpen &&
        !deferAddCardModal &&
        (useNewCard || !hasSavedCards) ? (
          <div className="rounded-xl border border-border bg-muted/20 p-4 mb-4">
            <CardSection
              stripeErr={stripeErr}
              validateErr={validateErr}
              variant="modern"
            />
          </div>
        ) : null} */}

        {showAdminPricingBreakdown ? (
          <OrderSummaryBreakdown
            paymentData={pricingSummaryData}
            category={selectedCategory}
            className="mb-3 sm:mb-4"
            showPromoCode
            promoCodeValue={inlinePromoCode}
            onPromoCodeChange={setInlinePromoCode}
            onApplyPromoCode={applyPromoCode}
            onRemovePromoCode={removePromoCode}
            showRemovePromoCode={promoIsActive}
            promoApplying={getTotalAmountLoader}
            promoMessage={promoMessage}
            promoMessageIsError={promoMessageIsError}
          />
        ) : null}

        {/* Promo code (legacy non-admin path) */}
        {!showAdminPricingBreakdown && addPromoCode ? (
          <div className="rounded-xl border border-border bg-card p-2.5 sm:p-4 mb-3 sm:mb-4 w-full min-w-0">
            <label className="text-sm text-start font-medium text-foreground mb-2 block">
              Promo code
            </label>
            <div className="flex flex-col sm:flex-row gap-2 w-full min-w-0">
              <Input
                value={inlinePromoCode}
                placeholder="Enter promo code"
                onChange={(e) => setInlinePromoCode(e.target.value)}
                className="flex-1 min-w-0 w-full"
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                className="w-full sm:w-auto shrink-0 bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90"
                onClick={applyPromoCode}
                disabled={!inlinePromoCode.trim() || getTotalAmountLoader}
              >
                {getTotalAmountLoader ? "Applying…" : "Apply"}
              </Button>
            </div>
            {appliedPromoCode || promoMessage ? (
              <p
                className={`text-sm text-start mt-2 ${
                  promoMessageIsError
                    ? "text-destructive"
                    : "text-emerald-700"
                }`}
              >
                {appliedPromoCode ? `Applied: ${appliedPromoCode}` : null}
                {promoMessage
                  ? `${appliedPromoCode ? " — " : ""}${promoMessage}`
                  : ""}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Total due today */}
        {!showAdminPricingBreakdown && totalDueToday != null ? (
          <div className="flex items-center justify-between rounded-xl bg-secondary/5 border border-secondary/20 px-4 py-3">
            <span className="text-sm font-medium text-foreground">
              Total due today
            </span>
            <span className="text-lg font-semibold text-secondary">
              ${totalDueToday}
            </span>
          </div>
        ) : null}

        {/* Stripe branding footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 mt-2 border-t border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            <span>
              Secure payments powered by{" "}
              <span className="font-semibold" style={{ color: "#635BFF" }}>
                Stripe
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded text-[10px] font-bold text-white bg-[#EB001B]">
              MC
            </span>
            <span className="px-2 py-1 rounded text-[10px] font-bold text-white bg-[#1A1F71]">
              VISA
            </span>
            <span className="px-2 py-1 rounded text-[10px] font-bold text-white bg-[#006FCF]">
              AMEX
            </span>
          </div>
        </div>
      </div>

      {/* Add new card modal */}
      {isAddCardModalOpen ? (
        <DialogBox
          width="sm"
          openPopup={isAddCardModalOpen}
          setOpenPopup={setIsAddCardModalOpen}
          // Admin AddCardForm already renders its own close control; keep the
          // dialog close only for non-admin so we don't stack two X buttons.
          onClose={
            from_admin ? undefined : () => setIsAddCardModalOpen(false)
          }
          modalOverApex
        >
          <Elements stripe={stripePromise}>
            <AddCardForm
              openPopup={isAddCardModalOpen}
              isTailwindComponent
              handleCloseAddCardModal={() => {
                setIsAddCardModalOpen(false);
                refreshCards();
              }}
              onClose={() => setIsAddCardModalOpen(false)}
              callListingAPI={refreshCards}
              fromAdmin={from_admin}
              uid={uid}
            />
          </Elements>
        </DialogBox>
      ) : null}

      <Loader open={reqLoader || statesLoader || cardLoader} />
    </div>
  );
};

export default InlinePaymentCheckout;
